/**
 * Trader Dashboard Page
 * Main trading interface with market watchlist, chart, and positions
 * Desktop: Watchlist (left) | Chart (center) | Financial Bar (bottom)
 * Mobile: Dedicated mobile layout with bottom navigation
 */
import { useState, useEffect } from 'react';
import { useTraderTheme } from '../context/TraderThemeContext';
import { MobileNavProvider, useMobileNav } from '../context/MobileNavContext';
import { useWebSocket } from '../../context/WebSocketContext';
import TraderLayout from '../components/TraderLayout';
import MarketWatchlist from '../components/MarketWatchlist';
import ChartContainer from '../components/ChartContainer';
import FinancialBar from '../components/FinancialBar';
import PositionsTable from '../components/PositionsTable';
import NewOrderModal from '../components/NewOrderModal';
import MobileTraderLayout from '../components/MobileTraderLayout';
import './desktop-workspace.css';

// Desktop Dashboard Content
const DesktopDashboardContent = () => {
  const { isDark } = useTraderTheme();
  const { prices } = useWebSocket();
  const [selectedInstrument, setSelectedInstrument] = useState({ symbol: 'EUR/USD' });
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [orderInstrument, setOrderInstrument] = useState(null);

  const handleInstrumentClick = (instrument) => {
    setSelectedInstrument(instrument);
    setOrderInstrument(instrument);
    setOrderModalOpen(true);
  };

  const handleInstrumentSelect = (instrument) => {
    setSelectedInstrument(instrument);
  };

  // Get current price data for the selected instrument with fallback for slashed/unslashed keys
  const sym = selectedInstrument?.symbol || '';
  const cleanSym = sym ? sym.replace('/', '').toUpperCase() : '';
  const slashSym = (cleanSym.length === 6 && !sym.includes('/')) ? `${cleanSym.slice(0, 3)}/${cleanSym.slice(3)}` : sym;
  const currentPriceData = sym ? (prices[sym] || (slashSym ? prices[slashSym] : null) || (cleanSym ? prices[cleanSym] : null)) : null;

  return (
    <div className="tr-desktop-workspace h-full flex flex-col" data-theme={isDark ? 'dark' : 'light'} data-testid="desktop-trading-workspace">
      {/* Main Content Area */}
      <div className="tr-desktop-workspace-main flex-1 flex min-h-0" data-testid="desktop-trading-main">
        {/* Market Watchlist - Left Panel */}
        <MarketWatchlist 
          selectedInstrument={selectedInstrument}
          onSelectInstrument={handleInstrumentSelect}
          onInstrumentClick={handleInstrumentClick}
        />

        {/* Chart Area - Center */}
        <div className="tr-desktop-chart-column flex-1 min-w-0" data-testid="desktop-chart-column">
          <div className={`tr-desktop-chart min-h-0 rounded-lg overflow-hidden border ${
            isDark ? 'border-gray-700' : 'border-gray-200'
          }`} data-testid="desktop-chart-panel">
            <ChartContainer 
              fitContainer
              symbol={selectedInstrument?.symbol || 'EUR/USD'} 
              instrumentData={selectedInstrument}
              priceData={currentPriceData}
            />
          </div>

          {/* Positions Table - Below Chart */}
          <div className="tr-desktop-positions" data-testid="desktop-positions-panel">
            <PositionsTable />
          </div>
        </div>
      </div>

      {/* Financial Info Bar - Bottom */}
      <FinancialBar />

      {/* New Order Modal */}
      <NewOrderModal 
        isOpen={orderModalOpen}
        onClose={() => setOrderModalOpen(false)}
        instrument={orderInstrument}
      />
    </div>
  );
};

// Responsive Dashboard Wrapper
const DashboardWrapper = () => {
  const { isMobile, isTablet } = useMobileNav();
  
  // On mobile/tablet, use the dedicated mobile layout
  if (isMobile || isTablet) {
    return <MobileTraderLayout />;
  }
  
  // On desktop, use the standard layout with sidebar
  return (
    <TraderLayout>
      <DesktopDashboardContent />
    </TraderLayout>
  );
};

const TraderDashboard = () => {
  return (
    <MobileNavProvider>
      <DashboardWrapper />
    </MobileNavProvider>
  );
};

export default TraderDashboard;
